import { divisionApi } from "@src/api/dictionaries/divisions";
import type { ApiDivision } from "@src/api/dictionaries/divisions";
import { usersApi } from "./api.core";
import type { ApiSessionUser, ApiUserGroup, ApiUserIncludeField, ApiUserListFilter, ApiUserListItem } from "./types";

/** Расширенный фильтр пользователей с `Include`. */
export interface ApiUserLoadFilter extends ApiUserListFilter {
    Include?: ApiUserIncludeField[];
}

function normalizeIncludeCodes(codes: number[] | null | undefined): number[] {
    return codes ?? [];
}

function mapExpandedDivisions(divisionCodes: number[], divisions: Map<number, ApiDivision>): ApiDivision[] {
    return divisionCodes.reduce<ApiDivision[]>((acc, code) => {
        const division = divisions.get(code);
        if (division != null) {
            acc.push(division);
        }
        return acc;
    }, []);
}

function mapExpandedGroups(groupCodes: number[], groups: Map<number, ApiUserGroup>): ApiUserGroup[] {
    return groupCodes.reduce<ApiUserGroup[]>((acc, code) => {
        const group = groups.get(code);
        if (group != null) {
            acc.push(group);
        }
        return acc;
    }, []);
}

function toSessionUser(item: ApiUserListItem, divisions: ApiDivision[], userGroups: ApiUserGroup[]): ApiSessionUser {
    return {
        ...item,
        DivisionCodes: normalizeIncludeCodes(item.DivisionCodes),
        GroupCodes: normalizeIncludeCodes(item.GroupCodes),
        Divisions: divisions,
        UserGroups: userGroups,
    };
}

/**
 * Сервисный слой пользователей поверх `user.loadListByFilter`. \
 * Expand подразделений и групп по `Include` (`DivisionCodes` / `GroupCodes`).
 */
export const userServices = {
    /**
     * Загружает пользователей через {@link usersApi.loadListByFilter}
     * и при необходимости expand `DivisionCodes` / `GroupCodes`.
     * @param params фильтр и `Include`
     * @param config опции HTTP-запроса
     */
    load: async (
        params: ApiUserLoadFilter,
        config?: Parameters<typeof usersApi.loadListByFilter>[1],
    ): Promise<ApiSessionUser[]> => {
        const apiRes = await usersApi.loadListByFilter(params, config);
        if (params.Include == null) {
            return apiRes.map((item) => toSessionUser(item, [], []));
        }

        const includeDivision = params.Include.includes("DivisionCodes");
        const includeGroup = params.Include.includes("GroupCodes");

        const divisionCodes = new Set<number>();
        const groupCodes = new Set<number>();

        apiRes.forEach((user) => {
            if (includeDivision) {
                normalizeIncludeCodes(user.DivisionCodes).forEach((code) => divisionCodes.add(code));
            }
            if (includeGroup) {
                normalizeIncludeCodes(user.GroupCodes).forEach((code) => groupCodes.add(code));
            }
        });

        const divisions = new Map<number, ApiDivision>();
        const groups = new Map<number, ApiUserGroup>();

        const loadPromises: Promise<void>[] = [];

        if (includeDivision && divisionCodes.size > 0) {
            loadPromises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }).then(({ items }) => {
                    items.forEach((item) => divisions.set(item.Code, item));
                }),
            );
        }

        if (includeGroup && groupCodes.size > 0) {
            loadPromises.push(
                usersApi.loadGroupsByFilter({ Codes: [...groupCodes] }, config).then((items) => {
                    items.forEach((item) => groups.set(item.Code, item));
                }),
            );
        }

        await Promise.all(loadPromises);

        return apiRes.map((item) => {
            const normalizedDivisionCodes = normalizeIncludeCodes(item.DivisionCodes);
            const normalizedGroupCodes = normalizeIncludeCodes(item.GroupCodes);

            return toSessionUser(
                item,
                includeDivision ? mapExpandedDivisions(normalizedDivisionCodes, divisions) : [],
                includeGroup ? mapExpandedGroups(normalizedGroupCodes, groups) : [],
            );
        });
    },
} as const;
